<template>
<md-whiteframe md-elevation="2" class="project-box-v2">
    <div class="pb-header">
        <button
            type="button"
            class="pb-pin"
            :class="{ 'is-pinned': isPinned }"
            :aria-pressed="isPinned ? 'true' : 'false'"
            :aria-label="isPinned ? 'Unpin project' : 'Pin project'"
            @click.stop="togglePin"
        >
            <md-icon>push_pin</md-icon>
            <md-tooltip>{{ isPinned ? 'Unpin' : 'Pin' }}</md-tooltip>
        </button>
        <span class="pb-title" @click="selectProject">
            {{ project.title }}
            <md-tooltip class="pb-title-tooltip">{{ project.title }}</md-tooltip>
        </span>
        <dashboard-kebab-menu
            ref="kebab"
            :project="project"
            @projectAccepted="handleProjectAccepted"
            @projectDeleted="handleProjectDeleted"
        />
    </div>
    <div class="pb-cover" @click="selectProject">
        <span v-if="project.unlocked" class="pb-showpass" title="Unlocked with a Show Pass">Show Pass</span>
        <span
            v-else-if="isLegacy"
            class="pb-legacy"
            :class="{ 'is-owner': isLegacyOwner }"
            :title="legacyTip"
            :aria-label="'Legacy project. ' + legacyTip"
            :role="isLegacyOwner ? 'button' : null"
            :tabindex="isLegacyOwner ? 0 : null"
            @click.stop="onLegacyBadge"
            @keydown.enter.stop.prevent="onLegacyBadge"
        >Legacy</span>
        <!-- Plain svg + native title rather than the vue-material icon/tooltip
             pair: matches how .pb-showpass already labels itself, and adds no
             new vue-material for Stage D to remove. Native title also responds
             to long-press on touch, which the hover-only tooltip does not. -->
        <span
            v-if="hasActiveShowSync"
            class="pb-showsync"
            :class="showSyncStateClass"
            :title="showSyncTooltip"
            :aria-label="showSyncTooltip"
            role="img"
        >
            <svg viewBox="0 0 24 24" width="15" height="15" aria-hidden="true" focusable="false">
                <path fill="currentColor" d="M12 4V1L8 5l4 4V6a6 6 0 0 1 5.19 8.99l1.47 1.47A8 8 0 0 0 12 4zm0 14a6 6 0 0 1-5.19-8.99L5.34 7.54A8 8 0 0 0 12 20v3l4-4-4-4v3z"/>
            </svg>
        </span>
        <img v-if="hasPoster" :src="project.posterUrl" :alt="project.title" @error="onPosterError" />
        <div v-else class="pb-generated" :style="{ backgroundColor: coverColor }">
            <span>{{ coverInitials }}</span>
        </div>
    </div>
    <div class="pb-footer" @click="selectProject">
        Last updated · {{ lastChanged }}
    </div>
</md-whiteframe>
</template>

<script>
/**
 * Grid tile for a project on the redesigned dashboard.
 * Title always visible; pin toggle on-card; custom poster or generated cover;
 * per-project actions (Duplicate/Delete) live in the shared kebab menu.
 */
import moment from 'moment';
import { mapGetters } from 'vuex';
import projectNav from '@/mixins/projectNav';
import projectCover from '@/mixins/projectCover';
import showSync from '@/mixins/showSync';
import DashboardKebabMenu from '@/components/dashboard/kebab-menu';

const ProjectBox = {
    name: 'ProjectBox',
    mixins: [projectNav, projectCover, showSync],
    props: ['project'],
    computed: {
        ...mapGetters(['user']),
        // Legacy tool (Josh, 5-6 Oct 2026): projects that still open in the legacy editor carry a "Legacy" badge in the
        // Show Pass spot (a Show Pass project is always current, so the two never meet). The owner clicks it to move the
        // project; anyone else just gets the tooltip.
        isLegacy() {
            return !!(this.project && this.project.isNew !== true && !this.project.isSample);
        },
        isLegacyOwner() {
            return this.isLegacy && !!this.user && this.user.uid === this.project.ownerUID;
        },
        legacyTip() {
            return this.isLegacyOwner ? 'Move to the current editor\u2026' : 'Opens in the legacy editor';
        },
        lastChanged() {
            const d = this.project.lastChangedDate ? moment(this.project.lastChangedDate) : moment();
            return d.format('MMM D, YYYY');
        },
        isPinned() {
            const up = this.user && this.user.projects && this.user.projects[this.project.id];
            return !!(up && up.pinned);
        }
    },
    methods: {
        onLegacyBadge() {
            if (this.isLegacyOwner && this.$refs.kebab) this.$refs.kebab.openMove();
        },
        togglePin() {
            this.$store.dispatch('togglePinProject', {
                projectId: this.project.id,
                pinned: !this.isPinned
            }).catch((e) => {
                console.error('Error toggling pin:', e);
                if (this.$toast) this.$toast.error('Could not update pin. Please try again.');
            });
        },
        handleProjectAccepted(id) {
            this.$emit('projectAccepted', id);
        },
        handleProjectDeleted(id) {
            this.$emit('projectDeleted', id);
        }
    },
    components: {
        'dashboard-kebab-menu': DashboardKebabMenu
    }
};
export default ProjectBox;
</script>

<style lang="scss">
.project-box-v2 {
    background: #fff;
    border-radius: 6px;
    overflow: visible;
    display: flex;
    flex-direction: column;
    height: 100%;
    width: 100%;
    min-width: 0;
    box-sizing: border-box;

    .pb-header {
        display: flex;
        align-items: center;
        padding: 8px 4px 8px 8px;
        gap: 4px;
        min-width: 0; /* allow the title to shrink/ellipsize instead of widening the card */

        .pb-pin {
            background: none;
            border: none;
            padding: 2px;
            cursor: pointer;
            display: inline-flex;
            flex: 0 0 auto;
            border-radius: 50%;
            i {
                font-size: 18px !important;
                color: #b0b0b0;
                transform: rotate(45deg);
            }
            &.is-pinned i { color: var(--accent); transform: rotate(0deg); }
            &:hover { background: rgba(0, 0, 0, 0.06); }
            &:focus { outline: 2px solid var(--accent); outline-offset: 1px; }
        }
        .pb-title {
            flex: 1 1 auto;
            min-width: 0; /* required for text-overflow: ellipsis inside a flex row */
            font-size: 16px;
            font-weight: 600;
            color: #333;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
            cursor: pointer;
        }
    }

    .pb-cover {
        position: relative;
        width: 100%;
        padding-top: 100%; /* square */
        cursor: pointer;
        /* Show Sync marker. Lower-right so it never collides with .pb-showpass
           (top-left) or the kebab menu (in .pb-header, above the cover).
           Colour comes from the design-system accent tokens rather than a
           literal teal, so it inverts correctly in dark mode: --accent-ink is
           the deep teal in light mode and the light teal under
           .md-theme-show-mode. */
        .pb-showsync {
            position: absolute;
            right: 8px;
            bottom: 8px;
            z-index: 1;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            width: 24px;
            height: 24px;
            border-radius: 50%;
            background: rgba(255, 255, 255, 0.92);

            /* Both colours are design-system tokens with dark-mode variants, so
               they invert on their own under .md-theme-show-mode. */
            &.is-active {
                color: var(--accent-ink, #12718a);
            }
            &.is-failed {
                color: var(--danger, #c0392b);
            }
            box-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);
            /* NOT pointer-events:none, unlike .pb-showpass — the native title
               only appears on an element that receives pointer events. The
               marker sits inside the clickable .pb-cover and has no click
               handler of its own, so clicks still bubble up and open the
               project. Tooltip works, tile stays fully clickable. */
        }
        .pb-showpass {
            position: absolute;
            top: 8px;
            left: 8px;
            z-index: 1;
            background: #0d2744;
            color: #fff;
            font-size: 11px;
            font-weight: 700;
            letter-spacing: 0.03em;
            text-transform: uppercase;
            padding: 3px 8px;
            border-radius: 10px;
            box-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);
            pointer-events: none;
        }
        .pb-legacy {
            position: absolute;
            top: 8px;
            left: 8px;
            z-index: 1;
            background: #fff;
            color: #12718a;
            font-size: 11px;
            font-weight: 700;
            letter-spacing: 0.03em;
            text-transform: uppercase;
            padding: 3px 8px;
            border-radius: 10px;
            box-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);
            cursor: default;
            &.is-owner { cursor: pointer; }
            &.is-owner:hover, &.is-owner:focus-visible { outline: 2px solid #12718a; outline-offset: 1px; }
        }
        img {
            position: absolute;
            top: 0; left: 0;
            width: 100%; height: 100%;
            object-fit: cover;
        }
        .pb-generated {
            position: absolute;
            top: 0; left: 0;
            width: 100%; height: 100%;
            display: flex;
            align-items: center;
            justify-content: center;
            span {
                color: #fff;
                font-size: 48px;
                font-weight: 700;
                letter-spacing: 1px;
            }
        }
    }

    .pb-footer {
        padding: 10px 8px;
        font-size: 13px;
        color: #666;
        cursor: pointer;
    }
}

.md-theme-show-mode {
    .project-box-v2 {
        background: #37474f;
        .pb-header .pb-title { color: rgba(255, 255, 255, 0.92); }
        .pb-footer { color: rgba(255, 255, 255, 0.7); }
        .pb-header .pb-pin i { color: #90a4ae; }
        .pb-header .pb-pin.is-pinned i { color: var(--accent); }
        .pb-cover .pb-legacy { background: #263238; color: #73d5e3; }
        .pb-cover .pb-legacy.is-owner:hover, .pb-cover .pb-legacy.is-owner:focus-visible { outline-color: #73d5e3; }
    }
}
</style>


// WEBPACK FOOTER //
// project-box.vue?77fbd3be